import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { users } from '@/api/users';
import { PageHeader } from '@/components/PageHeader';
import { DataTable, type Column } from '@/components/DataTable';
import { RoleBadge } from '@/components/RoleBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { UserRow } from '@/api/users';
import { UserPlus, Pencil } from 'lucide-react';
import { formatRef } from '@/lib/formatRef';

export function UserList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({ queryKey: ['users'], queryFn: () => users.list() });

  const columns: Column<UserRow>[] = [
    {
      header: 'ID',
      className: 'w-20 shrink-0',
      cell: (r) => (
        <span className="font-mono text-xs text-muted-foreground">
          {r.ref_number ? formatRef(r.ref_number, 'USR') : '—'}
        </span>
      ),
    },
    {
      header: t('users.columnEmail'),
      cell: (r) => (
        <Link to={`/users/${r.ref_number ?? r.id}`} className="font-medium text-foreground hover:text-primary hover:underline">
          {r.email}
        </Link>
      ),
    },
    { header: t('users.columnName'), cell: (r) => r.display_name ?? <span className="text-muted-foreground">—</span> },
    { header: t('users.columnRole'), cell: (r) => <RoleBadge role={r.role} /> },
    {
      header: t('users.columnStatus'),
      cell: (r) => (
        <Badge variant={r.active ? 'default' : 'secondary'}>
          {r.active ? t('common.active') : t('common.inactive')}
        </Badge>
      ),
    },
    {
      header: t('users.columnLastLogin'),
      cell: (r) => (
        <span className="text-muted-foreground tabular-nums">
          {r.last_login_at ? new Date(r.last_login_at).toLocaleString() : '—'}
        </span>
      ),
    },
    {
      header: '',
      className: 'w-10 text-end',
      cell: (r) => (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
          aria-label={`Edit ${r.email}`}
          onClick={(e) => { e.stopPropagation(); navigate(`/users/${r.ref_number ?? r.id}`); }}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow={t('nav.users')}
        title={t('users.title')}
        description={t('users.description')}
        actions={
          <Button asChild>
            <Link to="/users/new">
              <UserPlus className="h-4 w-4" /> {t('users.inviteUser')}
            </Link>
          </Button>
        }
      />
      {isLoading ? <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
        : error ? <div className="text-sm text-destructive">{(error as Error).message}</div>
        : <DataTable rows={data?.rows ?? []} columns={columns} empty={t('users.noUsersYet')} />}
    </>
  );
}
